import React from "react";

import type { ISelectProps } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import * as Yup from "yup";

import {
	ClientSelect,
	runsClient,
} from "@/components/molecules/ClientSelect/ClientSelect";

import {
	MAX_TICKET_PREFIXES,
	TICKET_PREFIX,
	TICKET_PREFIXES_CHIP_PROPS,
} from "@/constants/CProjects";

import type { TCompany, TSection } from "@/types";

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

type TClientValues = { internal?: boolean; client?: TCompany | null };

/**
 * A function, not a constant: `t()` is i18next's plain export, so the
 * caller must run this after i18next.init() (inside the component),
 * or every label resolves to its raw key.
 */
/** `workspaceId`: the project's - it moves only between clients of its own workspace. */
export const PROJECT_SETTINGS_SECTIONS = (workspaceId?: number): TSection[] => [
	{
		key: "identity",
		title: t("projects.settings.title"),
		validationSchema: Yup.object().shape({
			name: Yup.string().trim().required(t("forms.required")),
			color: Yup.string().matches(HEX_COLOR, {
				message: t("forms.invalid"),
				excludeEmptyString: true,
			}),
			description: Yup.string().max(
				1000,
				t("forms.max", { value: 1000 })
			),
		}),
		fields: [
			{
				name: "name",
				type: "input",
				label: t("projects.namePlaceholder"),
				required: true,
			},
			{ name: "color", type: "color", label: t("company.color") },
			{
				name: "description",
				type: "textarea",
				label: t("projects.description"),
			},
		],
	},
	// Who the work is for. `internal` hides the client and the rate; the
	// tab turns these three into clientId / internal / rate on submit.
	{
		key: "client",
		title: t("projects.client.title"),
		validationSchema: Yup.object().shape({
			client: Yup.mixed().when("internal", {
				is: false,
				then: (schema) =>
					schema.nonNullable(t("projects.client.required")),
				otherwise: (schema) => schema.nullable(),
			}),
			rate: Yup.number().nullable().min(0, t("forms.invalid")),
		}),
		fields: [
			{
				name: "internal",
				type: "toggle",
				label: t("projects.client.internal"),
				valueDisplay: (values) =>
					(values as TClientValues).internal
						? t("actions.yes")
						: t("actions.no"),
			},
			{
				name: "client",
				type: "select",
				label: t("client.label"),
				options: [],
				hidden: (values) => !!(values as TClientValues).internal,
				renderer: (props) => {
					const { value, onChange, error } =
						props as ISelectProps<TCompany>;
					return (
						<ClientSelect
							value={value as TCompany | null}
							onChange={onChange}
							error={error}
							filter={(client) =>
								runsClient(client) &&
								(workspaceId === undefined ||
									client.workspace?.id === workspaceId)
							}
						/>
					);
				},
				valueDisplay: (values) =>
					(values as TClientValues).client?.name ?? "—",
			},
			{
				name: "rate",
				type: "number",
				label: t("projects.client.rate"),
				min: 0,
				decimals: 2,
				after: "€",
				hidden: (values) => !!(values as TClientValues).internal,
				valueDisplay: (values) => {
					const rate = (values as { rate?: number | null }).rate;
					return rate === null || rate === undefined
						? t("projects.client.rateHint")
						: `${rate} €`;
				},
			},
		],
	},
	// Which ticket keys the reports badge.
	{
		key: "tickets",
		title: t("projects.tickets.title"),
		validationSchema: Yup.object().shape({
			ticketPrefixes: Yup.array()
				.of(
					Yup.string().matches(
						TICKET_PREFIX,
						t("projects.tickets.invalid")
					)
				)
				.max(MAX_TICKET_PREFIXES),
		}),
		fields: [
			{
				name: "ticketPrefixes",
				type: "chips",
				label: t("projects.tickets.prefixes"),
				placeholder: t("projects.tickets.placeholder"),
				...TICKET_PREFIXES_CHIP_PROPS,
				valueDisplay: (values) =>
					(
						(values as { ticketPrefixes?: string[] })
							.ticketPrefixes ?? []
					).join(", ") || t("projects.tickets.none"),
			},
		],
	},
];
